import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import { FormikProps } from "formik";
import UserForm from "./user-form";
import { TypeUser } from "../../../interface/enum/TypeUser";
import RelatedUser from "./RelatedUser/related-user";
import { IAppUser } from "../../../interface/IUser";
import { IAutoCompleteLabelValue } from "../../../interface/enum/GeneralType";
import BankAccountForm from "./BankAccount/bank-account-form";
import UserInfo from "./UserInfo/user-info";
import WebSystemUser from "./WebSystem/web-system-user";
import { TextField, Typography } from "@mui/material";
import Transactions from "../../../pages/transactions/transactions";
import { calculateBalance } from "../../../utilities";
import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "src/context/auth";
import ActionButtons from "./ActionUserButtons";
import UserAgreements from "./UserAgreements";
import SmsHistoryPage from "src/pages/sms/sms-history";
import { getUnreadInboundSmsCountByPhone } from "src/api/sms-history";

// Props برای کامپوننت TabPanel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// کامپوننت TabPanel برای نمایش محتوای هر تب
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// تابع کمکی برای ایجاد props مورد نیاز برای تب‌ها
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

// Props برای کامپوننت اصلی TabInfoUser
interface TabInfoUserProps {
  formik: FormikProps<any>;
  usersData: IAppUser[];
  onClose: () => void;
  isEdit?: boolean;
  userData?: IAppUser;
  deletedUserAsync: UseMutateAsyncFunction<any, Error, any, unknown>;
  deleteLoading: boolean;
  insertAsync: UseMutateAsyncFunction<any, Error, any, unknown>;
  insertLoading: boolean;
  updateAsync: UseMutateAsyncFunction<any, Error, any, unknown>;
  updateLoading: boolean;
  refetchUser: () => void;
  tags?: IAutoCompleteLabelValue[];
}

export default function TabInfoUser({
  formik,
  usersData,
  isEdit = false,
  userData,
  deleteLoading,
  deletedUserAsync,
  insertAsync,
  insertLoading,
  updateAsync,
  updateLoading,
  onClose,
  refetchUser,
  tags,
}: TabInfoUserProps) {
  // State برای مدیریت تب فعال
  const [value, setValue] = React.useState(0);
  // State برای تعداد قراردادها
  const [agreementsCount, setAgreementsCount] = React.useState<number>(0);

  const { user } = useAuth();

  // تابع بررسی اینکه آیا کاربر مشتری است یا نه
  const isCustomer = React.useCallback(() => {
    // بررسی از formik
    const formikIsCustomer = formik.values.roleName === TypeUser.CUSTOMER;
    // بررسی از userData
    const userDataIsCustomer = userData?.roleName === TypeUser.CUSTOMER;
    // بررسی از userRole در userData (اگر وجود داشته باشد)
    const userRoleIsCustomer = userData?.roleName === "Customer";

    return formikIsCustomer || userDataIsCustomer || userRoleIsCustomer;
  }, [formik.values.roleName, userData?.roleName]);

  // دریافت تعداد پیام‌های خوانده نشده مشتری بر اساس شماره موبایل
  const { data: unreadSmsCount = 0 } = useQuery({
    queryKey: ["unread-sms-count", userData?.phoneNumber],
    queryFn: () =>
      userData?.phoneNumber
        ? getUnreadInboundSmsCountByPhone(
            user?.token || "",
            userData.phoneNumber
          )
        : Promise.resolve(0),
    enabled: !!user?.token && !!userData?.phoneNumber && isCustomer(),
    refetchInterval: 30000, // هر 30 ثانیه به‌روزرسانی می‌شود
  });

  // استفاده از useRef برای جلوگیری از حلقه بی‌نهایت
  const formikRef = React.useRef(formik);
  const refetchUserRef = React.useRef(refetchUser);
  const onCloseRef = React.useRef(onClose);

  // به‌روزرسانی ref ها
  React.useEffect(() => {
    formikRef.current = formik;
    refetchUserRef.current = refetchUser;
    onCloseRef.current = onClose;
  });

  // تابع تغییر تب
  const handleChange = React.useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);
    },
    []
  );

  // تابع ذخیره‌سازی با به‌روزرسانی اطلاعات
  const handleSave = React.useCallback(async () => {
    await formikRef.current.submitForm();
    // بعد از ذخیره‌سازی موفق، اطلاعات کاربر را به‌روزرسانی کن
    if (
      Object.keys(formikRef.current.errors).length === 0 &&
      !formikRef.current.isSubmitting
    ) {
      refetchUserRef.current();
    }
  }, []); // dependency array خالی

  // تابع ذخیره‌سازی و بستن دیالوگ
  const handleSaveAndClose = React.useCallback(async () => {
    await formikRef.current.submitForm();
    // بعد از ذخیره‌سازی موفق، اطلاعات کاربر را به‌روزرسانی کن
    if (
      Object.keys(formikRef.current.errors).length === 0 &&
      !formikRef.current.isSubmitting
    ) {
      refetchUserRef.current();
    }
    onCloseRef.current();
  }, []); // dependency array خالی

  // محاسبه تعداد آیتم‌ها برای نمایش در عنوان تب‌ها
  const getTabLabel = React.useCallback(
    (baseLabel: string, items?: any[], countField?: string) => {
      if (!items || items.length === 0) return baseLabel;
      return `${baseLabel} (${items.length})`;
    },
    []
  );

  // تابع به‌روزرسانی تعداد قراردادها
  const handleAgreementsUpdate = React.useCallback((count: number) => {
    setAgreementsCount(count);
  }, []);

  // تنظیم تعداد قراردادها در ابتدا
  React.useEffect(() => {
    if (userData?.customerAgreements) {
      setAgreementsCount(userData.customerAgreements.length);
    }
  }, [userData?.customerAgreements]);

  // محاسبه مانده حساب برای نمایش در تب
  const getBalanceLabel = React.useCallback(() => {
    if (!userData?.transactions) return "مانده حساب";
    const balance = calculateBalance(
      userData.transactions.filter((t) => t.transactionStatus === 1) ?? []
    );
    return `مانده حساب (${balance.toLocaleString()} ریال)`;
  }, [userData?.transactions]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* نوار تب‌ها */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="تب‌های اطلاعات کاربر"
        >
          {/* تب اطلاعات شخصی */}
          <Tab label="اطلاعات شخصی" {...a11yProps(0)} />

          {/* تب سامانه‌ها - فقط برای مشتریان */}
          {formik.values.roleName === TypeUser.CUSTOMER && (
            <Tab
              label={getTabLabel("سامانه ها", userData?.systems)}
              {...a11yProps(1)}
            />
          )}

          {/* تب افراد مرتبط - فقط برای مشتریان */}
          {formik.values.roleName === TypeUser.CUSTOMER && (
            <Tab
              label={getTabLabel("افراد مرتبط", userData?.childs)}
              {...a11yProps(2)}
            />
          )}

          {/* تب حساب‌های بانکی - فقط برای مشتریان */}
          {formik.values.roleName === TypeUser.CUSTOMER && (
            <Tab
              label={getTabLabel("حساب های بانکی", userData?.bankAccountsUser)}
              {...a11yProps(3)}
            />
          )}

          {/* تب راه‌های ارتباطی */}
          <Tab
            label={getTabLabel("راه های ارتباطی", userData?.addressesUser)}
            {...a11yProps(4)}
          />

          {/* تب توضیحات */}
          <Tab
            label={"توضیحات" + (formik.values.desc ? " (دارد)" : "")}
            {...a11yProps(5)}
          />

          {/* تب مانده حساب - فقط برای مشتریان */}
          {isCustomer() && <Tab label={getBalanceLabel()} {...a11yProps(6)} />}

          {/* تب قراردادها - فقط برای مشتریان */}
          {isCustomer() && (
            <Tab
              label={`قرارداد ها${agreementsCount > 0 ? ` (${agreementsCount})` : ""}`}
              {...a11yProps(7)}
            />
          )}

          {/* تب وظایف مرتبط - فقط در حالت ویرایش */}
          {isEdit && <Tab label="وظایف مرتبط" {...a11yProps(8)} />}

          {/* تب پیگیری‌های دوره‌ای - فقط در حالت ویرایش */}
          {isEdit && <Tab label="پیگیری های دوره ای" {...a11yProps(9)} />}
          {isCustomer() && isEdit && userData?.id && (
            <Tab
              label={
                <Badge
                  badgeContent={unreadSmsCount > 0 ? unreadSmsCount : undefined}
                  color="error"
                  max={99}
                >
                  <span>پیامک ها</span>
                </Badge>
              }
              {...a11yProps(10)}
            />
          )}
        </Tabs>
      </Box>

      {/* محتوای تب اطلاعات شخصی */}
      <CustomTabPanel value={value} index={0}>
        <Box sx={{ mb: 2 }}>
          <ActionButtons
            onSave={handleSave}
            onSaveAndClose={handleSaveAndClose}
            isLoading={insertLoading || updateLoading || deleteLoading}
          />
        </Box>
        <UserForm
          usersData={usersData}
          formik={formik}
          isEdit={isEdit}
          tags={tags}
        />
      </CustomTabPanel>

      {/* محتوای تب سامانه‌ها */}
      <CustomTabPanel value={value} index={1}>
        <WebSystemUser
          isEdit={isEdit}
          userWebsystems={userData?.systems}
          userRef={userData?.id}
          onClose={onClose}
          refetchUser={refetchUser}
        />
      </CustomTabPanel>

      {/* محتوای تب افراد مرتبط */}
      <CustomTabPanel value={value} index={2}>
        <RelatedUser
          onClose={onClose}
          userRef={userData?.id}
          childsUser={userData?.childs}
          deletedUserAsync={deletedUserAsync}
          insertAsync={insertAsync}
          updateAsync={updateAsync}
          deleteLoading={deleteLoading}
          insertLoading={insertLoading}
          updateLoading={updateLoading}
          userData={userData}
        />
      </CustomTabPanel>

      {/* محتوای تب حساب‌های بانکی */}
      <CustomTabPanel value={value} index={3}>
        <BankAccountForm
          onClose={onClose}
          userData={userData}
          refetchUser={refetchUser}
        />
      </CustomTabPanel>

      {/* محتوای تب راه‌های ارتباطی */}
      <CustomTabPanel value={value} index={4}>
        <UserInfo
          userData={userData}
          onClose={onClose}
          refetchUser={refetchUser}
        />
      </CustomTabPanel>

      {/* محتوای تب توضیحات */}
      <CustomTabPanel value={value} index={5}>
        <Box sx={{ mb: 2 }}>
          <ActionButtons
            onSave={handleSave}
            onSaveAndClose={handleSaveAndClose}
            isLoading={insertLoading || updateLoading || deleteLoading}
          />
        </Box>
        <TextField
          {...formik.getFieldProps("desc")}
          multiline
          rows={20}
          fullWidth
          label="توضیحات"
          placeholder="توضیحات مربوط به کاربر را وارد کنید..."
        />
      </CustomTabPanel>

      {/* محتوای تب مانده حساب - فقط برای مشتریان */}
      {isCustomer() && userData?.id && (
        <CustomTabPanel value={value} index={6}>
          <Transactions userId={userData?.id} />
        </CustomTabPanel>
      )}

      {/* محتوای تب قراردادها - فقط برای مشتریان */}
      {isCustomer() && userData?.id && (
        <CustomTabPanel value={value} index={7}>
          <UserAgreements
            userId={Number(userData.id)}
            onClose={onClose}
            refetchUser={refetchUser}
            onAgreementsUpdate={handleAgreementsUpdate}
          />
        </CustomTabPanel>
      )}

      {/* محتوای تب وظایف مرتبط */}
      <CustomTabPanel value={value} index={8}>
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            لیست وظایف مرتبط در حال توسعه است
          </Typography>
        </Box>
      </CustomTabPanel>

      {/* محتوای تب پیگیری‌های دوره‌ای */}
      <CustomTabPanel value={value} index={9}>
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            لیست پیگیری‌های دوره‌ای در حال توسعه است
          </Typography>
        </Box>
      </CustomTabPanel>

      {/* محتوای تب پیامک‌ها */}
      {isCustomer() && isEdit && userData?.id && (
        <CustomTabPanel value={value} index={10}>
          <SmsHistoryPage defaultPhone={userData.phoneNumber} hideFilters />
        </CustomTabPanel>
      )}
    </Box>
  );
}
